/* eslint-disable no-restricted-syntax */
/**
 * @fileoverview Theme-wide helpers loaded as the global `Utility` (see `snippets/script-tags.liquid`).
 *
 * **Namespaces** (stable public API — do not rename keys):
 * - {@link Utility.Observer} — `IntersectionObserver` helpers.
 * - {@link Utility.URL} — query strings and `history` updates.
 * - {@link Utility.Animate} — fades, rAF updates, loading state, vibration.
 * - {@link Utility.Focus} — focusable queries, trap, restore (modern drawer/modal flow).
 * - {@link Utility.Fetch} — `fetch` + JSON/HTML/section helpers with shared error reporting.
 * - {@link Utility.Storage} — cookie, `localStorage`, `sessionStorage`.
 * - {@link Utility.DOM} — parse HTML, patch nodes, motion-aware scroll, click guards.
 * - {@link Utility.CartUi} — header cart count from cart-like JSON.
 * - {@link Utility.ThemeLoader} — ref-counted full-page loader host.
 * - {@link Utility.Form} — serialize and validate forms.
 *
 * **Top-level methods**: {@link Utility.debounce}, {@link Utility.toggleElement}, {@link Utility.forceFocus},
 * {@link Utility.removeTrapFocus}, {@link Utility.trapFocus} (legacy cart), {@link Utility.announceToScreenReader},
 * {@link Utility.formatMoney}, {@link Utility.reportError}.
 */

/**
 * @typedef {object} UtilityHistoryUrlOptions
 * @property {object} [state] - Opaque state for `history.pushState` / `replaceState`.
 * @property {string} [path] - Pathname (defaults to `window.location.pathname`).
 */

/**
 * @typedef {object} UtilityRemoveHashOptions
 * @property {object} [state]
 * @property {boolean} [keepQuery] - Keep `location.search` when stripping the hash (default: `true`).
 */

/** @type {{ focusin?: (event: FocusEvent) => void, focusout?: () => void, keydown?: (event: KeyboardEvent) => void }} */
const trapFocusHandlers = {};
const Utility = {

  /**
   * Helpers around `IntersectionObserver` with shared defaults (`root`, `rootMargin`, `threshold`).
   */
  Observer: {
    /**
     * Creates an `IntersectionObserver` with theme defaults (`root`, `rootMargin`, `threshold`).
     *
     * @param {IntersectionObserverCallback} callback - Invoked when visibility changes.
     * @param {IntersectionObserverInit} [options] - Merged over defaults.
     * @returns {IntersectionObserver}
     */
    create(callback, options = {}) {
      const defaultOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
        ...options
      };
      return new IntersectionObserver(callback, defaultOptions);
    },

    /**
     * Observes until the target intersects once, then disconnects.
     *
     * @param {Element|string} element - Target or CSS selector.
     * @param {(target: Element, entry: IntersectionObserverEntry) => void} onVisible - Called on first intersection.
     * @param {IntersectionObserverInit} [options] - Passed to `create`.
     * @returns {IntersectionObserver}
     */
    once(element, onVisible, options = {}) {
      const callback = (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(entry.target, entry);
            observer.disconnect();
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (target) observer.observe(target);
      
      return observer;
    },

    /**
     * Observes enter and exit transitions for a single target.
     *
     * @param {Element|string} element - Target or CSS selector.
     * @param {(target: Element, entry: IntersectionObserverEntry) => void} [onEnter]
     * @param {(target: Element, entry: IntersectionObserverEntry) => void} [onExit]
     * @param {IntersectionObserverInit} [options] - Passed to `create`.
     * @returns {IntersectionObserver}
     */
    onChange(element, onEnter, onExit, options = {}) {
      const callback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && onEnter) {
            onEnter(entry.target, entry);
          } else if (!entry.isIntersecting && onExit) {
            onExit(entry.target, entry);
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (target) observer.observe(target);
      
      return observer;
    }
  },

  /**
   * Query string parsing and `history` helpers for filters and collection URLs.
   */
  URL: {
    /**
     * Reads a single query parameter from a search string or URL segment.
     *
     * @param {string} name - Parameter name.
     * @param {string} [url=window.location.search] - `?foo=bar` string or full URL segment containing params.
     * @returns {string|null}
     */
    getParam(name, url = window.location.search) {
      const params = new URLSearchParams(url);
      return params.get(name);
    },

    /**
     * Serializes a plain object or `FormData` to `application/x-www-form-urlencoded` query text.
     *
     * @param {Record<string, string|number|boolean|undefined|null>|FormData} data - Key/value pairs; empty values are skipped for objects.
     * @returns {string} Query string without a leading `?`.
     */
    buildQuery(data) {
      if (data instanceof FormData) {
        return new URLSearchParams(Array.from(data)).toString();
      }
      
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
      return params.toString();
    },

    /**
     * Pushes a new history entry whose query is `queryString` (or path-only if empty).
     *
     * @param {string} queryString - Encoded query without `?`, or empty for path-only.
     * @param {UtilityHistoryUrlOptions} [options] - Optional `state` and `path`.
     * @returns {void}
     */
    updateUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.pushState(state, '', newUrl);
    },

    /**
     * Replaces the current URL (no new history entry).
     *
     * @param {string} queryString - Encoded query without `?`, or empty for path-only.
     * @param {UtilityHistoryUrlOptions} [options] - Optional `state` and `path`.
     * @returns {void}
     */
    replaceUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.replaceState(state, '', newUrl);
    },

    /**
     * Strips `location.hash` via `history.replaceState` (optional `state`, optional preserved search).
     *
     * @param {UtilityRemoveHashOptions} [options]
     * @returns {void}
     */
    removeHash(options = {}) {
      const { state = {}, keepQuery = true } = options;
      const path = window.location.pathname;
      const search = keepQuery ? window.location.search : '';
      const newUrl = `${path}${search}`;
      window.history.replaceState(state, '', newUrl);
    }
  },

  /**
   * Lightweight opacity/display animations, rAF scheduling, busy states, and `navigator.vibrate`.
   */
  Animate: {
    /**
     * Steps opacity toward 0/1 using `requestAnimationFrame` (legacy; does not wait for completion before optional callback).
     *
     * @private
     * @param {Element} elem - Target element.
     * @param {'fadeIn'|'fadeOut'} action - Direction.
     * @returns {void}
     */
    _fadeEffect(elem, action) {
      const FADE_STEP = 0.01;

      if (action === 'fadeIn') {
        elem.style.display = 'block';
        elem.style.opacity = 0;
        const fadeIn = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue < 1) {
            newValue += FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeIn);
          } else {
            elem.style.opacity = 1;
          }
        };
        requestAnimationFrame(fadeIn);
      } else if (action === 'fadeOut') {
        elem.style.opacity = 1;
        const fadeOut = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue > 0) {
            newValue -= FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeOut);
          } else {
            elem.style.opacity = 0;
            elem.style.display = 'none';
          }
        };
        requestAnimationFrame(fadeOut);
      }
    },

    /**
     * Fades a node in (`display: block`, opacity toward 1). `callback` runs immediately after starting the loop, not after finish.
     *
     * @param {Element|string} element - Node or selector.
     * @param {() => void} [callback] - Invoked right after `_fadeEffect` is scheduled.
     * @returns {void}
     */
    fadeIn(element, callback) {
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (!target) return;
      
      this._fadeEffect(target, 'fadeIn');
      if (callback) {
        callback();
      }
    },

    /**
     * Fades a node out (opacity toward 0, then `display: none`). `callback` timing matches {@link Utility.Animate.fadeIn}.
     *
     * @param {Element|string} element - Node or selector.
     * @param {() => void} [callback] - Invoked right after `_fadeEffect` is scheduled.
     * @returns {void}
     */
    fadeOut(element, callback) {
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (!target) return;
      
      this._fadeEffect(target, 'fadeOut');
      if (callback) {
        callback();
      }
    },

    /**
     * Fades `element` in, waits `duration` ms, then fades out.
     *
     * @param {Element|string} element - Node or selector.
     * @param {number} [duration=3000] - Visible time in milliseconds.
     * @returns {void}
     */
    showMessage(element, duration = 3000) {
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (!target) return;
      
      this.fadeIn(target, () => {
        setTimeout(() => {
          this.fadeOut(target);
        }, duration);
      });
    },

    /**
     * Runs `updateFn` on the next animation frame with the resolved element (used to batch DOM reads/writes).
     *
     * @param {Element|string} element - Node or selector.
     * @param {(el: Element) => void} updateFn - Called as `updateFn(target)` after rAF.
     * @returns {void}
     */
    smooth(element, updateFn) {
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (!target) return;
      
      requestAnimationFrame(() => updateFn(target));
    },

    /**
     * Toggles `loading` class and `aria-disabled` on buttons/links.
     *
     * @param {Element|string} element - Node or selector.
     * @param {boolean} isLoading - When true, marks busy; when false, clears.
     * @returns {void}
     */
    setLoading(element, isLoading) {
      const target = typeof element === 'string' ? document.querySelector(element) : element;
      if (!target) return;

      if (isLoading) {
        target.setAttribute('aria-disabled', true);
        target.classList.add('loading');
      } else {
        target.classList.remove('loading');
        target.removeAttribute('aria-disabled');
      }
    },

    /**
     * Calls `navigator.vibrate` when available (no-op otherwise).
     *
     * @param {number|number[]} [pattern=100] - Duration in ms or Vibration API pattern.
     * @returns {void}
     * @example Utility.Animate.vibrate([100, 50, 100]);
     */
    vibrate(pattern = 100) {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    }
  },

  /**
   * Focus rings, programmatic focus with retry, and focus traps for overlays (capture-phase listeners).
   */
  Focus: {
    /** @type {Record<string, (event: Event) => void>} */
    _handlers: {},
    /** @type {Element|null} */
    _activeContainer: null,
    /** @type {Element|null} */
    _previouslyFocused: null,
    /** @type {ReturnType<typeof setTimeout>|null} */
    _forceTimeout: null,

    /**
     * True when the element is not `display:none`, not `visibility:hidden`, and opacity is not `0`.
     *
     * @private
     * @param {Element} element - Candidate node.
     * @returns {boolean}
     */
    _isVisible(element) {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    },

    /**
     * Returns focusable descendants (theme-tuned selector list) optionally including visually hidden nodes.
     *
     * @param {Element|null|undefined} container - Root to search within.
     * @param {boolean} [includeHidden=false] - When true, skips the visibility filter.
     * @returns {Element[]}
     */
    getFocusableElements(container, includeHidden = false) {
      if (!container) {
        console.warn('Focus.getFocusableElements: No container provided');
        return [];
      }

      const selector = [
        // Custom theme elements
        '.filter__container',
        // Links
        'a[href]',
        'a[data-href]',
        // Buttons and inputs
        '[type="button"]',
        'button:enabled',
        'input:not([type=hidden]):not([tabindex^="-"]):enabled',
        'select:enabled',
        'textarea:enabled',
        // Interactive elements
        'summary',
        '[contenteditable]:not([contenteditable="false"])',
        'details',
        // Media with controls
        'audio[controls]',
        'video[controls]',
        // Embedded content
        'embed',
        'object',
        'iframe',
        // Map area
        'area[href]',
        // Custom focusable
        '[tabindex]:not([tabindex^="-"])',
        '[draggable="true"]'
      ].join(', ');

      const elements = Array.from(container.querySelectorAll(selector));

      return elements.filter((el) => {
        if (el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return false;
        return includeHidden || this._isVisible(el);
      });
    },

    /**
     * Focuses with optional `preventScroll`; retries once after `timeout` using temporary `tabindex="0"` if needed.
     *
     * @param {Element|null|undefined} element - Target.
     * @param {{ preventScroll?: boolean, timeout?: number }} [options] - `preventScroll` and retry delay (default 500).
     * @returns {boolean} Whether focus appears successful immediately (retry may still run).
     */
    force(element, options = {}) {
      const { preventScroll = false, timeout = 500 } = options;

      if (!element || !this._isVisible(element)) {
        console.warn('Focus.force: Invalid or invisible element', element);
        return false;
      }

      if (this._forceTimeout) clearTimeout(this._forceTimeout);

      element.focus({ preventScroll });
      if (element === document.activeElement) return true;

      // Retry with tabindex
      const hadTabindex = element.hasAttribute('tabindex');
      const originalTabindex = element.getAttribute('tabindex');
      element.setAttribute('tabindex', '0');
      
      this._forceTimeout = setTimeout(() => {
        element.focus({ preventScroll });
        if (!hadTabindex) {
          element.removeAttribute('tabindex');
        } else if (originalTabindex !== '0') {
          element.setAttribute('tabindex', originalTabindex);
        }
        if (element !== document.activeElement) {
          console.warn('Focus.force: Unable to focus after retry');
        }
      }, timeout);

      return element === document.activeElement;
    },

    /**
     * Installs a focus trap on `container` (Tab cycles first↔last); optional initial focus target.
     *
     * @param {Element} container - Root element whose focusables are cycled.
     * @param {Element|null} [elementToFocus] - First focus target (defaults to first focusable).
     * @param {{ returnFocus?: boolean, skipVisibilityCheck?: boolean, includeHidden?: boolean }} [options] - Trap behavior.
     * @returns {boolean} Whether the trap was applied.
     */
    trap(container, elementToFocus = null, options = {}) {
      const { returnFocus = true, skipVisibilityCheck = false, includeHidden = false } = options;

      if (!container) return false;
      if (!skipVisibilityCheck && !this._isVisible(container)) {
        requestAnimationFrame(() => setTimeout(() => this.trap(container, elementToFocus, { ...options, skipVisibilityCheck: true, includeHidden: true }), 50));
        return false;
      }

      const checkHidden = includeHidden || skipVisibilityCheck;
      const elements = this.getFocusableElements(container, checkHidden);
      if (!elements.length) { container.setAttribute('tabindex', '-1'); return false; }

      if (returnFocus && document.activeElement !== document.body) this._previouslyFocused = document.activeElement;
      this.release(null, false);
      this._activeContainer = container;

      this._handlers.keydown = (e) => {
        if (e.key !== 'Tab' && e.keyCode !== 9) return;
        const current = this.getFocusableElements(container, checkHidden);
        if (!current.length || !container.contains(document.activeElement)) return;
        const [first, last] = [current[0], current[current.length - 1]];
        if ((document.activeElement === last && !e.shiftKey) || (document.activeElement === first && e.shiftKey)) {
          e.preventDefault();
          (document.activeElement === last ? first : last).focus();
        }
      };

      this._handlers.focusin = (e) => {
        if (this._activeContainer && !this._activeContainer.contains(e.target)) {
          e.preventDefault(); e.stopPropagation();
          this.getFocusableElements(container, checkHidden)[0]?.focus();
        }
      };

      document.addEventListener('keydown', this._handlers.keydown, true);
      document.addEventListener('focusin', this._handlers.focusin, true);
      requestAnimationFrame(() => this.force(elementToFocus || elements[0]));
      return true;
    },

    /**
     * Tears down capture listeners from {@link Utility.Focus.trap} and optionally restores prior focus.
     *
     * @param {Element|null} [elementToFocus] - Explicit post-trap target.
     * @param {boolean} [usePrevious=true] - When true, fall back to the element focused before `trap`.
     * @returns {void}
     */
    release(elementToFocus = null, usePrevious = true) {
      // Remove event listeners with capture flag
      ['focusin', 'keydown'].forEach(type => {
        if (this._handlers[type]) {
          document.removeEventListener(type, this._handlers[type], true);
        }
      });

      this._handlers = {};
      this._activeContainer = null;

      if (this._forceTimeout) {
        clearTimeout(this._forceTimeout);
        this._forceTimeout = null;
      }

      const returnElement = elementToFocus || (usePrevious ? this._previouslyFocused : null);
      
      if (returnElement?.isConnected) {
        try {
          returnElement.focus();
        } catch (error) {
          console.warn('Focus.release: Unable to return focus', error);
        }
      }

      this._previouslyFocused = null;
    },

    /**
     * @returns {boolean} True when {@link Utility.Focus.trap} is active.
     */
    isTrapped() {
      return this._activeContainer !== null;
    },

    /**
     * @returns {Element|null} Container passed to the active trap, if any.
     */
    getActiveContainer() {
      return this._activeContainer;
    },

    /**
     * Builds a small descriptor (`#id`, label `for`, or `href`) for re-querying after HTML swaps.
     *
     * @param {Element|null} [element] - Defaults to `document.activeElement`.
     * @param {Element|null} [container] - When set, returns null if `element` is outside this subtree.
     * @returns {{ selector: string }|{ element: Element }|null}
     */
    storeForRestore(element = null, container = null) {
      const activeElement = element || document.activeElement;
      if (!activeElement) return null;
      
      // Check if element is within container if provided
      if (container && !container.contains(activeElement)) return null;
      
      // Store element identifier - prefer ID, fallback to selector
      if (activeElement.id) {
        return { selector: `#${activeElement.id}` };
      }
      if (activeElement.getAttribute('for')) {
        return { selector: `label[for="${activeElement.getAttribute('for')}"]` };
      }
      if (activeElement.href) {
        return { selector: `a[href="${activeElement.href}"]` };
      }
      // Fallback to element reference if still connected
      return activeElement.isConnected ? { element: activeElement } : null;
    },

    /**
     * Focuses a node described by {@link Utility.Focus.storeForRestore} (next frame).
     *
     * @param {{ selector?: string, element?: Element }|null|undefined} storedElement - Descriptor from `storeForRestore`.
     * @param {ParentNode} [context=document] - Root for `querySelector` when using `selector`.
     * @returns {boolean} True when a connected target was found.
     */
    restore(storedElement, context = document) {
      if (!storedElement) return false;
      
      let elementToFocus = null;
      
      if (storedElement.selector) {
        elementToFocus = (context || document).querySelector(storedElement.selector);
      } else if (storedElement?.element?.isConnected) {
        elementToFocus = storedElement.element;
      }
      
      if (elementToFocus?.isConnected) {
        requestAnimationFrame(() => {
          elementToFocus.focus();
        });
        return true;
      }
      
      return false;
    }
  },

  /**
   * Logs to the console, surfaces `ThemeToast.error` when available, sets `utilityReported` on the `Error`, then throws.
   *
   * @param {Error|unknown} error - Caught value or `Error`.
   * @param {string} contextLabel - `console.error` prefix (e.g. module name).
   * @param {string} [toastTitle='Error'] - Toast heading.
   * @returns {never}
   */
  reportError(error, contextLabel, toastTitle = 'Error') {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error(contextLabel, err);
    const message = err.message?.trim() || 'Something went wrong';
    if (typeof ThemeToast !== 'undefined' && ThemeToast.error) {
      ThemeToast.error(message, toastTitle);
    }
    err.utilityReported = true;
    throw err;
  },

  /**
   * `fetch` wrappers that normalize failures through {@link Utility.reportError} (except rethrown `utilityReported` errors in `json`/`html`).
   */
  Fetch: {
    /**
     * Performs a request; non-OK responses become an `Error` with `status`, `response`, and parsed `data` when possible.
     *
     * @param {string|URL} url - Request URL.
     * @param {RequestInit} [options] - Standard `fetch` init.
     * @returns {Promise<Response|undefined>} Resolved `Response` on success; `reportError` throws so this is normally `Response`.
     */
    async request(url, options = {}) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          error.response = response;

          try {
            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const data = isJson ? await response.json() : await response.text();
            error.data = data;

            if (isJson) {
              const message = data?.message?.trim() || data?.description?.trim();
              if (message) error.message = message;
            } else if (data?.trim()) {
              error.message = data.trim();
            }
          } catch (parseError) {
            error.parseError = parseError;
          }

          throw error;
        }
        return response;
      } catch (error) {
        Utility.reportError(error, 'Fetch error:', 'Request Failed');
      }
    },

    /**
     * `request` + `response.json()`.
     *
     * @param {string|URL} url
     * @param {RequestInit} [options]
     * @returns {Promise<object|undefined>}
     */
    async json(url, options = {}) {
      try {
        const response = await this.request(url, options);
        return await response.json();
      } catch (error) {
        if (error?.utilityReported) {
          throw error;
        }
        Utility.reportError(error, 'Fetch JSON:', 'Request Failed');
      }
    },

    /**
     * `request` + `response.text()` (HTML or other text).
     *
     * @param {string|URL} url
     * @param {RequestInit} [options]
     * @returns {Promise<string|undefined>}
     */
    async html(url, options = {}) {
      try {
        const response = await this.request(url, options);
        return await response.text();
      } catch (error) {
        if (error?.utilityReported) {
          throw error;
        }
        Utility.reportError(error, 'Fetch HTML:', 'Request Failed');
      }
    },

    /**
     * Loads Section Rendering API JSON (`?sections=id` or comma-separated ids) via {@link Utility.Fetch.json}.
     *
     * @param {string} url - Base URL (may already contain `?`).
     * @param {string|string[]} sectionIds - One or more section IDs.
     * @returns {Promise<object|undefined>} Map of section id → HTML string (Shopify response shape).
     */
    async section(url, sectionIds) {
      const sections = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds;
      const separator = url.includes('?') ? '&' : '?';
      const sectionUrl = `${url}${separator}sections=${sections}`;
      return await this.json(sectionUrl);
    },

    /**
     * GET `/products/{handle}` (optional `?view=`) as HTML text.
     *
     * @param {string} handle - Product handle.
     * @param {string|null} [view] - Alternate Liquid view name.
     * @returns {Promise<string|undefined>}
     */
    async product(handle, view = null) {
      const url = view ? `/products/${handle}?view=${view}` : `/products/${handle}`;
      return this.html(url);
    },

    /**
     * Default POST init for Shopify Ajax APIs (`json` vs cart `javascript` / form posts).
     *
     * @param {'json'|'javascript'} [type='json'] - Sets `Accept` and adjusts `Content-Type` (removed for `javascript`).
     * @param {{ headers?: Record<string, string>, body?: BodyInit }} [config] - Extra headers and body.
     * @returns {{ method: string, headers: HeadersInit, body?: BodyInit }}
     */
    config(type = 'json', config = {}) {
      /** @type {Headers} */
      const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

      if (type === 'javascript') {
        headers['X-Requested-With'] = 'XMLHttpRequest';
        delete headers['Content-Type'];
      }

      return {
        method: 'POST',
        headers: /** @type {HeadersInit} */ (headers),
        body: config.body,
      };
    }
  },

  /**
   * Namespaced cookie, `localStorage`, and `sessionStorage` helpers (JSON wrappers for storage APIs).
   */
  Storage: {
    /** @namespace Utility.Storage.Cookie */
    Cookie: {
      /**
       * @param {string} name
       * @param {string|object} value
       * @param {{ days?: number, path?: string, domain?: string, secure?: boolean, sameSite?: string }} [options]
       * @returns {boolean}
       */
      set(name, value, options = {}) {
        const { days = 365, path = '/', domain = '', secure = false, sameSite = 'Lax' } = options;
        const val = typeof value === 'object' ? JSON.stringify(value) : String(value);
        const date = new Date(Date.now() + days * 864e5);
        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(val)}; expires=${date.toUTCString()}; path=${path}`;
        if (domain) cookie += `; domain=${domain}`;
        if (secure) cookie += '; secure';
        if (sameSite) cookie += `; samesite=${sameSite}`;
        document.cookie = cookie;
        return true;
      },

      /**
       * @param {string} name
       * @param {boolean} [parseJSON=false] - Parse value as JSON when true.
       * @returns {string|object|null}
       */
      get(name, parseJSON = false) {
        const nameEQ = `${encodeURIComponent(name)}=`;
        const cookie = document.cookie.split(';').find((c) => c.trim().indexOf(nameEQ) === 0);
        if (!cookie) return null;
        const value = decodeURIComponent(cookie.trim().substring(nameEQ.length));
        return parseJSON ? ((() => { try { return JSON.parse(value); } catch { return value; } })()) : value;
      },

      /**
       * @param {string} name
       * @param {{ days?: number, path?: string, domain?: string, secure?: boolean, sameSite?: string }} [options]
       * @returns {boolean}
       */
      remove(name, options = {}) {
        return this.set(name, '', { ...options, days: -1 });
      },

      /**
       * @param {string} name
       * @returns {boolean}
       */
      exists(name) {
        return this.get(name) !== null;
      },

      /**
       * @returns {Record<string, string>}
       */
      getAll() {
        const cookies = {};
        document.cookie.split(';').forEach((c) => {
          const [n, v] = c.trim().split('=');
          if (n) cookies[decodeURIComponent(n)] = decodeURIComponent(v || '');
        });
        return cookies;
      }
    },

    /** @namespace Utility.Storage.Local */
    Local: {
      /**
       * @param {string} key
       * @param {unknown} value
       * @param {{ ttl?: number }} [options] - Optional expiry timestamp offset (`ttl` ms).
       * @returns {boolean}
       */
      set(key, value, options = {}) {
        try {
          const data = { value, timestamp: Date.now(), ...(options.ttl && { expiry: Date.now() + options.ttl }) };
          localStorage.setItem(key, JSON.stringify(data));
          return true;
        } catch { return false; }
      },

      /**
       * @param {string} key
       * @param {unknown} [defaultValue=null]
       * @returns {unknown}
       */
      get(key, defaultValue = null) {
        try {
          const item = localStorage.getItem(key);
          if (!item) return defaultValue;
          const data = JSON.parse(item);
          if (data.expiry && Date.now() > data.expiry) {
            this.remove(key);
            return defaultValue;
          }
          return data.value;
        } catch {
          return localStorage.getItem(key) || defaultValue;
        }
      },

      /** @param {string} key @returns {boolean} */
      remove(key) { try { localStorage.removeItem(key); return true; } catch { return false; } },
      /** @returns {boolean} */
      clear() { try { localStorage.clear(); return true; } catch { return false; } },
      /** @param {string} key @returns {boolean} */
      exists(key) { return localStorage.getItem(key) !== null; },
      /** @returns {string[]} */
      keys() { return Object.keys(localStorage); },
      /** @returns {number} Rough byte-ish size estimate. */
      size() { return Object.keys(localStorage).reduce((s, k) => s + k.length + localStorage[k].length, 0); }
    },

    /** @namespace Utility.Storage.Session */
    Session: {
      /**
       * @param {string} key
       * @param {unknown} value
       * @returns {boolean}
       */
      set(key, value) {
        try {
          sessionStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
          return true;
        } catch { return false; }
      },

      /**
       * @param {string} key
       * @param {unknown} [defaultValue=null]
       * @returns {unknown}
       */
      get(key, defaultValue = null) {
        try {
          const item = sessionStorage.getItem(key);
          return item ? JSON.parse(item).value : defaultValue;
        } catch {
          return sessionStorage.getItem(key) || defaultValue;
        }
      },

      /** @param {string} key @returns {boolean} */
      remove(key) { try { sessionStorage.removeItem(key); return true; } catch { return false; } },
      /** @returns {boolean} */
      clear() { try { sessionStorage.clear(); return true; } catch { return false; } },
      /** @param {string} key @returns {boolean} */
      exists(key) { return sessionStorage.getItem(key) !== null; },
      /** @returns {string[]} */
      keys() { return Object.keys(sessionStorage); }
    }
  },

  /**
   * `DOMParser`, selective innerHTML swaps, and motion-aware scrolling helpers.
   */
  DOM: {
    /**
     * @param {string} htmlString - Full document snippet (Shopify section HTML, etc.).
     * @returns {Document}
     */
    parse(htmlString) {
      return new DOMParser().parseFromString(htmlString, 'text/html');
    },

    /**
     * Copies `innerHTML` from the first `source` match into the first `destination` match; toggles `d-none` when missing.
     *
     * @param {string} id - CSS selector (same for source tree and live DOM).
     * @param {Document} html - Parsed response document.
     * @param {ParentNode} [container=document] - Where to query the destination node.
     * @returns {void}
     */
    update(id, html, container = document) {
      const source = html.querySelector(id);
      const destination = container.querySelector(id);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      } else {
        destination?.classList.add('d-none');
      }
    },

    /**
     * Runs {@link Utility.DOM.update} for each selector in `selectors`.
     *
     * @param {string[]} selectors
     * @param {Document} sourceHTML
     * @param {ParentNode} [container=document]
     * @returns {void}
     */
    updateSelectors(selectors, sourceHTML, container = document) {
      selectors.forEach((selector) => this.update(selector, sourceHTML, container));
    },

    /**
     * Like {@link Utility.DOM.update} but for every matching node; extra destinations clone the first source.
     *
     * @param {string} selector
     * @param {Document} html
     * @param {ParentNode} [container=document]
     * @returns {void}
     */
    updateAll(selector, html, container = document) {
      const sources = html.querySelectorAll(selector);
      const destinations = container.querySelectorAll(selector);

      if (destinations.length === 0) {
        return;
      } else if (sources.length === 0) {
        destinations.forEach((dest) => dest.classList.add('d-none'));
        return;
      }

      destinations.forEach((destination, index) => {
        const source = sources[index] || sources[0];
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      });
    },

    /**
     * `querySelector` on a string (parsed first) or an existing document fragment.
     *
     * @param {string|Document} htmlString
     * @param {string} selector
     * @returns {Element|null}
     */
    extract(htmlString, selector) {
      const parsed = typeof htmlString === 'string' ? this.parse(htmlString) : htmlString;
      return parsed.querySelector(selector);
    },

    /**
     * True for a normal left-click without modifier keys (useful when intercepting `<a href>`).
     * @param {MouseEvent} event
     * @returns {boolean}
     */
    isPrimaryUnmodifiedClick(event) {
      if (!event || typeof event.button !== 'number') {
        return false;
      }

      return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
      );
    },

    /**
     * @returns {boolean} True when `(prefers-reduced-motion: reduce)` matches.
     */
    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /**
     * `scrollIntoView({ block: 'start' })` with `behavior` forced from {@link Utility.DOM.prefersReducedMotion}.
     *
     * @param {Element|null} element
     * @param {Object} [viewOptions] - Extra `scrollIntoView` options; `behavior` is always overridden.
     * @returns {void}
     */
    scrollIntoViewRespectingMotion(element, viewOptions = {}) {
      if (!element) {
        return;
      }

      const rest = { ...viewOptions };
      delete rest.behavior;
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';

      Utility.Animate.smooth(element, (el) => {
        el.scrollIntoView({ block: 'start', ...rest, behavior });
      });
    }
  },

  /**
   * Header cart bubble and other non-component cart UI driven by Ajax/JSON payloads.
   */
  CartUi: {
    /**
     * Updates `[data-cart-item-count]` text and parent `.cart-count-bubble` display from `item_count`.
     *
     * @param {{ item_count?: number } | null | undefined} cartLike - Cart JSON or section-derived object.
     * @returns {void}
     */
    syncHeaderFromCartLike(cartLike) {
      if (!cartLike || typeof cartLike.item_count !== 'number') {
        return;
      }

      const countEl = document.querySelector('[data-cart-item-count]');

      if (!countEl) {
        return;
      }

      countEl.textContent = String(cartLike.item_count);

      const bubble = countEl.closest('.cart-count-bubble');

      if (bubble) {
        bubble.style.display = cartLike.item_count > 0 ? '' : 'none';
      }
    },
  },

  /**
   * Ref-counted overlay bound to `theme-loader-host` (see layout / CSS for `.active`).
   */
  ThemeLoader: {
    /** @type {number} */
    _pendingCount: 0,
    /** @type {HTMLElement|null} */
    _host: null,

    /**
     * @private
     * @returns {HTMLElement|null}
     */
    _getHost() {
      if (!this._host) {
        this._host = document.querySelector('theme-loader-host');
      }

      return this._host;
    },

    /**
     * Increments the pending counter and shows the host when the first lock is acquired.
     *
     * @returns {void}
     */
    show() {
      this._pendingCount += 1;
      const host = this._getHost();

      if (!host) {
        return;
      }

      host.classList.add('active');
      host.setAttribute('aria-busy', 'true');
      host.setAttribute('aria-hidden', 'false');
    },

    /**
     * Decrements the pending counter; hides the host when it reaches zero.
     *
     * @returns {void}
     */
    hide() {
      this._pendingCount = Math.max(0, this._pendingCount - 1);

      if (this._pendingCount > 0) {
        return;
      }

      const host = this._getHost();

      if (!host) {
        return;
      }

      host.classList.remove('active');
      host.setAttribute('aria-busy', 'false');
      host.setAttribute('aria-hidden', 'true');
    }
  },

  /**
   * Form serialization and lightweight required-field validation with ARIA hints.
   */
  Form: {
    /**
     * Builds a plain object from `FormData` (line item `properties[...]` keys are nested under `properties`).
     *
     * @param {HTMLFormElement} form
     * @returns {Record<string, unknown> & { properties?: Record<string, FormDataEntryValue> }}
     */
    serialize(form) {
      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('Expected a form element');
      }

      const obj = {};
      const formData = new FormData(form);
      const regex = /^properties\[(.*?)\]$/;

      for (const key of formData.keys()) {
        const match = regex.exec(key);
        if (match) {
          obj.properties = obj.properties || {};
          obj.properties[match[1]] = formData.get(key);
        } else {
          obj[key] = formData.get(key);
        }
      }
      return obj;
    },

    /**
     * @param {string} email
     * @returns {boolean}
     */
    validateEmail(email) {
      return !!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    },

    /**
     * Marks empty `[required]` fields with `aria-invalid` / `aria-describedby` and collects human messages.
     *
     * @param {HTMLFormElement} form
     * @returns {string[]}
     */
    validate(form) {
      const errors = [];
      const requiredFields = form.querySelectorAll('[required]');

      requiredFields.forEach((field) => {
        if (!field.value.trim()) {
          field.setAttribute('aria-invalid', 'true');
          field.setAttribute('aria-describedby', `error-${field.id}`);
          errors.push(`${field.getAttribute('aria-label') || field.name} is required`);
        } else {
          field.removeAttribute('aria-invalid');
        }
      });

      return errors;
    }
  },

  /**
   * Returns a debounced function that runs `fn` after `wait` ms of inactivity (last call wins).
   *
   * @param {(...args: unknown[]) => void} fn - Callback to invoke after the quiet period.
   * @param {number} wait - Delay in milliseconds.
   * @returns {(...args: unknown[]) => void}
   */
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  /**
   * Toggles open/closed state on a collapsible region inside `container` (`.toggle-content` or `[data-type="content"]`).
   *
   * @param {Element} container - Wrapper that contains the toggle target.
   * @param {'open'|'close'} activity - Whether to expand or collapse.
   * @returns {void}
   */
  toggleElement(container, activity) {
    const contentBlock = container.querySelector('[data-type="content"]') || container.querySelector('.toggle-content');
    if (!contentBlock) return;

    if (activity == 'open') {
      contentBlock.classList.add('open');
      container.classList.add('open');
      contentBlock.removeAttribute('inert');
      contentBlock.setAttribute('aria-hidden', 'false');
    } else {
      contentBlock.classList.remove('open');
      container.classList.remove('open');
      contentBlock.setAttribute('inert', '');
      contentBlock.setAttribute('aria-hidden', 'true');
    }
  },
  /**
   * Focuses `element`; if focus fails, assigns `tabindex="0"` and retries after a short delay (legacy helper).
   *
   * @param {Element|null|undefined} element - Target element.
   * @returns {void}
   */
  forceFocus(element) {
    if (!element) return;
    
    element.focus();

    let isFocused = false;
    if (element == document.activeElement) {
      isFocused = true;
    }

    if (!isFocused) {
      element.setAttribute('tabindex', '0');
      setTimeout(() => {
        element.focus();
      }, 500);
    }
  },

  /**
   * Removes listeners registered by `Utility.trapFocus` so a new trap can be installed.
   *
   * @returns {void}
   */
  removeTrapFocus() {
    if (trapFocusHandlers.focusin) {
      document.removeEventListener('focusin', trapFocusHandlers.focusin);
      delete trapFocusHandlers.focusin;
    }
    if (trapFocusHandlers.focusout) {
      document.removeEventListener('focusout', trapFocusHandlers.focusout);
      delete trapFocusHandlers.focusout;
    }
    if (trapFocusHandlers.keydown) {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
      delete trapFocusHandlers.keydown;
    }
  },

  /**
   * Traps Tab navigation between the first and last focusable nodes inside `container` (legacy cart drawer helper).
   *
   * @param {Element} container - Region to keep focus within.
   * @param {Element} [elementToFocus=container] - Node to focus after installing the trap.
   * @returns {void}
   */
  trapFocus(container, elementToFocus = container) {
    const elements = Utility.Focus.getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    this.removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;

      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
      // On the last focusable element and tab forward, focus the first element.
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      //  On the first focusable element and tab backward, focus the last element.
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    elementToFocus.focus();

    if (
      elementToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  },

  /**
   * Lazily created `aria-live` hosts keyed by politeness (`polite` / `assertive`).
   * @private
   * @type {Record<string, HTMLDivElement>}
   */
  _liveRegions: {},

  /**
   * @private
   * @param {'polite'|'assertive'|string} priority - `aria-live` value.
   * @returns {HTMLDivElement}
   */
  _getLiveRegion(priority) {
    if (!this._liveRegions[priority]) {
      const region = document.createElement('div');
      region.setAttribute('aria-live', priority);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
      this._liveRegions[priority] = region;
    }
    return this._liveRegions[priority];
  },

  /**
   * Writes to a dedicated live region so SRs pick up the change after a short clearing delay.
   *
   * @param {string} message
   * @param {'polite'|'assertive'} [priority='polite'] - `aria-live` token; maps to `_getLiveRegion` bucket.
   * @returns {void}
   */
  announceToScreenReader(message, priority = 'polite') {
    const region = this._getLiveRegion(priority);
    
    // Clear previous message
    region.textContent = '';
    
    // Set new message after a brief delay (ensures screen readers detect change)
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  },

  /**
   * Formats a cent amount using Shopify-style placeholders (`{{ amount }}`, etc.) and theme `money_format`.
   *
   * @param {number|string} cents - Integer cents, or string (`.` stripped before parse).
   * @param {string} [format] - Override format; defaults to `skFramework.themeData.money_format` or `"${{ amount }}"`.
   * @returns {string}
   */
  formatMoney(cents, format) {
    format = format || window.skFramework.themeData.money_format || "${{ amount }}";
    if (typeof cents === 'string') cents = cents.replace('.', '');
    
    const formatWithDelimiters = (num, prec = 2, thou = ',', dec = '.') => {
      if (isNaN(num) || num == null) return 0;
      const fixed = (num / 100).toFixed(prec);
      const parts = fixed.split('.');
      return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thou}`) + (parts[1] ? dec + parts[1] : '');
    };

    const type = format.match(/\{\{\s*(\w+)\s*\}\}/)[1];
    const value = {
      'amount': formatWithDelimiters(cents, 2),
      'amount_no_decimals': formatWithDelimiters(cents, 0),
      'amount_with_comma_separator': formatWithDelimiters(cents, 2, '.', ','),
      'amount_no_decimals_with_comma_separator': formatWithDelimiters(cents, 0, '.', ',')
    }[type];

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }
}